import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MessageActionType, PlannerMessage, PlannerTaskTab } from '../types';
import { BULK_DISABLED_TABS, LEAVE_REQUEST_TASK_KEYS } from '../constants';
import { usePlannerMessagesStore } from '../store';
import useSubmitBatchAction from '../hooks/useSubmitBatchAction';
import useSubmitTaskAction from '../hooks/useSubmitTaskAction';
import RejectDialog from './RejectDialog';
import { BulkBarContainer, TonalButton } from './BulkActionBar.styles';

interface BulkActionBarProps {
  messages: PlannerMessage[];
}

const getSelectedMessages = (
  messages: PlannerMessage[],
  selectedTaskIds: string[]
) => messages.filter((m) => selectedTaskIds.includes(m.taskId));

const hasAction = (message: PlannerMessage, action: MessageActionType) =>
  message.actions?.some((a) => Object.values(a).includes(action as never));

const isLeaveRequest = (message: PlannerMessage) =>
  (LEAVE_REQUEST_TASK_KEYS as readonly string[]).includes(
    message.taskDefinitionKey
  );

const getLeaveRequestId = (message: PlannerMessage) =>
  message.taskVariables?.find((v) => v.name === 'leaveRequestId')?.value;

const BulkActionBar = ({ messages }: BulkActionBarProps) => {
  const { t } = useTranslation('common');
  const { tab, selectedTaskIds, clearSelection } = usePlannerMessagesStore();
  const { mutateAsync: batchAction, isLoading: batchLoading } =
    useSubmitBatchAction();
  const { mutateAsync: singleAction, isLoading: singleLoading } =
    useSubmitTaskAction();
  const isLoading = batchLoading || singleLoading;
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  const isBulkDisabled = BULK_DISABLED_TABS.includes(tab as never);

  const selectableMessages = messages.filter(
    (m) => m.active && m.actions?.length
  );

  if (isBulkDisabled || selectableMessages.length === 0) return null;

  const selected = getSelectedMessages(messages, selectedTaskIds);
  const hasSelection = selectedTaskIds.length > 0;

  const acceptableMessages = selected.filter(
    (m) =>
      !isLeaveRequest(m) &&
      (hasAction(m, MessageActionType.ACCEPT) ||
        hasAction(m, MessageActionType.APPROVE))
  );
  const rejectableMessages = selected.filter((m) =>
    hasAction(m, MessageActionType.REJECT)
  );
  const seenMessages = selected.filter((m) =>
    hasAction(m, MessageActionType.SEEN)
  );

  const canAccept = acceptableMessages.length > 0;
  const canReject = rejectableMessages.length > 0;
  const canSeen = seenMessages.length > 0;

  const isLeaveTab = tab === PlannerTaskTab.LEAVE_REQUESTS;
  const isLeaveSelection =
    isLeaveTab ||
    selected.every((m) =>
      (LEAVE_REQUEST_TASK_KEYS as readonly string[]).includes(
        m.taskDefinitionKey
      )
    );

  const handleBulkAccept = async () => {
    if (acceptableMessages.length === 0) return;

    const acceptAction = acceptableMessages.some((m) =>
      hasAction(m, MessageActionType.APPROVE)
    )
      ? MessageActionType.APPROVE
      : MessageActionType.ACCEPT;

    const ids = acceptableMessages.map((m) => m.taskId);
    try {
      await batchAction({ taskIds: ids, actionType: acceptAction });
    } catch {
      // Error snackbar is handled by the mutation's onError callback
    }
  };

  const handleBulkRejectConfirm = async (note: string) => {
    if (rejectableMessages.length === 0) return;

    const leaveMessages = rejectableMessages.filter(isLeaveRequest);
    const otherMessages = rejectableMessages.filter((m) => !isLeaveRequest(m));

    let hasSuccess = false;

    if (leaveMessages.length > 0) {
      const results = await Promise.allSettled(
        leaveMessages.map((m) => {
          const leaveRequestId = getLeaveRequestId(m);
          return singleAction({
            taskId: m.taskId,
            actionType: MessageActionType.REJECT,
            note,
            taskDefinitionKey: m.taskDefinitionKey,
            leaveRequestId
          });
        })
      );

      if (results.some((r) => r.status === 'fulfilled')) hasSuccess = true;
    }

    if (otherMessages.length > 0) {
      try {
        await batchAction({
          taskIds: otherMessages.map((m) => m.taskId),
          actionType: MessageActionType.REJECT,
          note
        });
        hasSuccess = true;
      } catch {
        // Error snackbar is handled by the mutation's onError callback
      }
    }

    if (hasSuccess) {
      clearSelection();
      setRejectDialogOpen(false);
    }
  };

  const handleBulkMarkAsRead = async () => {
    if (seenMessages.length === 0) return;
    const seenIds = seenMessages.map((m) => m.taskId);
    try {
      await batchAction({
        taskIds: seenIds,
        actionType: MessageActionType.SEEN
      });
    } catch {
      // Error snackbar is handled by the mutation's onError callback
    }
  };

  return (
    <BulkBarContainer>
      {canAccept && (
        <TonalButton
          size="small"
          variant="tonal"
          color="success"
          disabled={!hasSelection || isLoading}
          onClick={handleBulkAccept}
        >
          {t('plannerMessages.actions.accept')}
          {` (${acceptableMessages.length})`}
        </TonalButton>
      )}

      {canReject && (
        <TonalButton
          size="small"
          variant="tonal"
          color="error"
          disabled={!hasSelection || isLoading}
          onClick={() => setRejectDialogOpen(true)}
        >
          {t('plannerMessages.actions.reject')}
          {` (${rejectableMessages.length})`}
        </TonalButton>
      )}

      {canSeen && (
        <TonalButton
          size="small"
          variant="tonal"
          disabled={!hasSelection || isLoading}
          onClick={handleBulkMarkAsRead}
        >
          {t('plannerMessages.actions.seen')}
          {` (${seenMessages.length})`}
        </TonalButton>
      )}

      <RejectDialog
        open={rejectDialogOpen}
        variant={isLeaveSelection ? 'leave' : 'default'}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleBulkRejectConfirm}
        isLoading={isLoading}
      />
    </BulkBarContainer>
  );
};

export default BulkActionBar;
